import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import type { ZIndex } from '../../tokens/z-index';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../AlertDialog/AlertDialog';
import { Button } from '../Button/Button';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * `destructive` shifts the confirm button to the red palette.
   * `context-change` uses the warning (laranja-encanto) palette — for Step Veículo
   * context switch confirmations (Zero KM ↔ Sem FIPE ↔ Frota — D-PHASE14-4).
   * Both variants render with role=alertdialog (via Radix AlertDialog primitive)
   * because any confirmation is semantically an alert requiring explicit user decision.
   */
  variant?: 'default' | 'destructive' | 'context-change';
  /** When truthy, disables the confirm button and renders a spinner. */
  confirmLoading?: boolean;
  /**
   * Inline error message above the footer. Use it when the backend rejects
   * the action and you want to let the user retry from inside the dialog
   * rather than dismiss + re-open.
   */
  error?: string | null;
  /**
   * Override do tier de z-index. Default: `Z.MODAL` (400). Usar `Z.TOAST`
   * (600) quando o ConfirmDialog é disparado de dentro de um Sheet hospedado
   * em `Z.DROPDOWN_OVER_MODAL` (500) — caso contrário abre atrás do Sheet.
   */
  zIndex?: ZIndex;
  onConfirm: () => void | Promise<void>;
}

/**
 * Canonical confirmation dialog used across delete / deactivate / approve
 * flows. Built on `@radix-ui/react-alert-dialog`:
 * - role=alertdialog (screen readers announce as a blocking alert)
 * - Initial focus on Cancel (prevents accidental destructive actions)
 * - Outside click does NOT dismiss (user must explicitly confirm or cancel)
 * - Esc still dismisses
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'default',
  confirmLoading = false,
  error,
  zIndex,
  onConfirm,
}: ConfirmDialogProps) {
  const { t } = useTranslation('common');
  const resolvedConfirm = confirmLabel ?? t('actions.confirmar');
  const resolvedCancel = cancelLabel ?? t('actions.cancelar');

  const handleConfirm = async (event: React.MouseEvent) => {
    // Prevent Radix auto-close so async onConfirm can finish (and error can
    // render inline) before the dialog goes away. Callers control close
    // via onOpenChange after their mutation resolves.
    event.preventDefault();
    await onConfirm();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent zIndex={zIndex}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description ? <AlertDialogDescription>{description}</AlertDialogDescription> : null}
        </AlertDialogHeader>

        {error ? (
          <div
            role="alert"
            className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-body text-red-700"
          >
            {error}
          </div>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel>
            <Button variant="outline" disabled={confirmLoading}>
              {resolvedCancel}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction>
            <Button
              variant={variant === 'destructive' ? 'danger' : 'primary'}
              onClick={handleConfirm}
              isLoading={confirmLoading}
              // context-change uses primary (laranja) — same as default but signals
              // the user is switching context (Zero KM / Sem FIPE / Frota — D-PHASE14-4)
            >
              {resolvedConfirm}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
